/**
 * @file index.jsx
 * @brief Displays the current user's listings with bulk actions, filtering, and status management.
 */
import { useEffect, useState, useMemo, useCallback } from "react";
import { useUnifiedAuth as useAuth } from "../../hooks/useUnifiedAuth";
import { createApiClient } from "../../api/axios";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { truncateText } from "../../utils/truncateText";
import styles from "./account-listings.module.css";

const STATUS_CONFIG = {
    draft:    { key: "accountListings.statuses.draft", cls: "statusDraft" },
    pend_app: { key: "accountListings.statuses.pending", cls: "statusPending" },
    rejected: { key: "accountListings.statuses.rejected", cls: "statusRejected" },
    active:   { key: "accountListings.statuses.active", cls: "statusActive" },
    transaction_in_progress: { key: "accountListings.statuses.transactionInProgress", cls: "statusPending" },
    paused:   { key: "accountListings.statuses.paused", cls: "statusPaused" },
    sold:     { key: "accountListings.statuses.sold", cls: "statusSold" },
    archived: { key: "accountListings.statuses.archived", cls: "statusArchived" },
};

/**
 * Render the seller dashboard used to manage the authenticated user's listings.
 * @returns {JSX.Element} The account listings screen.
 */
export default function AccountListings() {
    const auth = useAuth();
    const { t } = useTranslation();
    const accessToken = auth.user?.access_token;
    const api  = useMemo(
        () => createApiClient({
            isAuthenticated: auth.isAuthenticated,
            user: accessToken ? { access_token: accessToken } : null,
        }),
        [accessToken, auth.isAuthenticated]
    );

    const [listings, setListings]               = useState([]);
    const [imagesMap, setImagesMap]             = useState({});
    const [selectedListings, setSelectedListings] = useState([]);

    const [page, setPage]               = useState(1);
    const [count, setCount]             = useState(0);
    const [next, setNext]               = useState(null);
    const [previous, setPrevious]       = useState(null);
    const [search, setSearch]           = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [loading, setLoading]         = useState(true);

    /* ── Bulk action eligibility ── */
    const sel = selectedListings;
    const getStatus = (id) => listings.find((x) => x.id === id)?.status;
    const canBulk = {
        pause:     sel.length > 0 && sel.every((id) => getStatus(id) === "active"),
        resume:    sel.length > 0 && sel.every((id) => getStatus(id) === "paused"),
        archive:   sel.length > 0 && sel.every((id) => getStatus(id) !== "archived"),
        unarchive: sel.length > 0 && sel.every((id) => getStatus(id) === "archived"),
        delete:    sel.length > 0,
    };

    /* ── Fetch ── */
    const fetchListings = useCallback(async (pageNumber = 1) => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ page: pageNumber, search });
            if (statusFilter) params.append("status", statusFilter);

            const res  = await api.get(`listings/own/?${params.toString()}`);
            const data = res.data;

            setListings(data.results);
            setCount(data.count);
            setNext(data.next);
            setPrevious(data.previous);

            const map = {};
            await Promise.all(
                data.results.map(async (listing) => {
                    if (listing.images?.length > 0) {
                        const imgRes = await fetch(listing.images[0].url, {
                            headers: { Authorization: `Bearer ${accessToken}` },
                        });
                        if (!imgRes.ok) return;
                        const blob = await imgRes.blob();
                        map[listing.id] = URL.createObjectURL(blob);
                    }
                })
            );
            setImagesMap(map);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [accessToken, api, search, statusFilter]);

    useEffect(() => {
        if (accessToken) fetchListings(page);
    }, [accessToken, fetchListings, page]);

    /* ── Helpers ── */
    const toggleSelect = (id) =>
        setSelectedListings((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );

    /**
        * Toggle selection for every listing shown on the current page.
        * @returns {void}
     */
    const toggleSelectAll = () => {
        if (selectedListings.length === listings.length) setSelectedListings([]);
        else setSelectedListings(listings.map((l) => l.id));
    };

    const deleteListings = async (ids) => {
        await Promise.all(ids.map((id) => api.delete(`listings/${id}/`)));
        fetchListings(page);
        setSelectedListings([]);
    };

    const updateStatus = async (ids, status) => {
        await Promise.all(ids.map((id) => api.patch(`listings/${id}/`, { status })));
        fetchListings(page);
        setSelectedListings([]);
    };

    const totalPages = Math.max(1, Math.ceil(count / 10));

    /* ── Render ── */
    return (
        <div className={styles.container}>

            <div className={styles.pageHeader}>
                <h2 className={styles.pageTitle}>{t("accountListings.title")}</h2>
                <Link to="/listings/new" className={styles.newBtn}>+ {t("accountListings.newListing")}</Link>
            </div>

            {/* ── Toolbar ── */}
            <div className={styles.toolbar}>
                <div className={styles.searchBox}>
                    <span className={styles.searchIcon}>🔎</span>
                    <input
                        className={styles.searchInput}
                        placeholder={t("accountListings.searchPlaceholder")}
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                    />
                </div>

                <select
                    className={styles.statusSelect}
                    value={statusFilter}
                    onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                >
                    <option value="">{t("accountListings.allStatuses")}</option>
                    {Object.entries(STATUS_CONFIG).map(([key, { key: labelKey }]) => (
                        <option key={key} value={key}>{t(labelKey)}</option>
                    ))}
                </select>
            </div>

            {/* ── Bulk action bar ── */}
            {sel.length > 0 && (
                <div className={styles.bulkBar}>
                    <span className={styles.bulkCount}>{t("common.selectedCount", { count: sel.length })}</span>
                    <button className={styles.bulkBtn} disabled={!canBulk.pause}     onClick={() => updateStatus(sel, "paused")}>{t("accountListings.pause")}</button>
                    <button className={styles.bulkBtn} disabled={!canBulk.resume}    onClick={() => updateStatus(sel, "active")}>{t("accountListings.resume")}</button>
                    <button className={styles.bulkBtn} disabled={!canBulk.archive}   onClick={() => updateStatus(sel, "archived")}>{t("accountListings.archive")}</button>
                    <button className={styles.bulkBtn} disabled={!canBulk.unarchive} onClick={() => updateStatus(sel, "pend_app")}>{t("accountListings.unarchive")}</button>
                    <button
                        className={`${styles.bulkBtn} ${styles.bulkBtnDanger}`}
                        disabled={!canBulk.delete}
                        onClick={() => window.confirm(t("accountListings.deleteSelectedConfirm")) && deleteListings(sel)}
                    >
                        {t("common.delete")}
                    </button>
                    <button className={styles.bulkClear} onClick={() => setSelectedListings([])}>✕ {t("common.clear")}</button>
                </div>
            )}

            {/* ── Listings ── */}
            {loading ? (
                <div className={styles.skeletonGrid}>
                    {[...Array(4)].map((_, i) => (
                        <div key={i} className={styles.skeletonCard}>
                            <div className={styles.skeletonImg} />
                            <div className={styles.skeletonBody}>
                                <div className={styles.skeletonLine} style={{ width: "55%" }} />
                                <div className={styles.skeletonLine} style={{ width: "25%" }} />
                                <div className={styles.skeletonLine} style={{ width: "40%" }} />
                            </div>
                        </div>
                    ))}
                </div>
            ) : listings.length === 0 ? (
                <div className={styles.empty}>
                    <span>🗂</span>
                    <p>{t("accountListings.noListings")}</p>
                </div>
            ) : (
                <>
                    {/* Select-all row */}
                    <div className={styles.selectAllRow}>
                        <label className={styles.selectAllLabel}>
                            <input
                                type="checkbox"
                                checked={selectedListings.length === listings.length && listings.length > 0}
                                onChange={toggleSelectAll}
                            />
                            {t("accountListings.selectAll")}
                        </label>
                        <span className={styles.countLabel}>{t("common.listingCount", { count })}</span>
                    </div>

                    <div className={styles.listings}>
                        {listings.map((listing) => {
                            const statusCfg = STATUS_CONFIG[listing.status] || {};
                            const isSelected = selectedListings.includes(listing.id);

                            return (
                                <div
                                    key={listing.id}
                                    className={`${styles.card} ${isSelected ? styles.cardSelected : ""}`}
                                >
                                    {/* Checkbox */}
                                    <label className={styles.checkboxLabel}>
                                        <input
                                            type="checkbox"
                                            className={styles.checkbox}
                                            checked={isSelected}
                                            onChange={() => toggleSelect(listing.id)}
                                        />
                                    </label>

                                    {/* Image */}
                                    <Link to={`/listings/${listing.id}`} className={styles.imageLink}>
                                        <div className={styles.image}>
                                            {imagesMap[listing.id] ? (
                                                <img src={imagesMap[listing.id]} alt={listing.title} />
                                            ) : (
                                                <div className={styles.placeholder}>{t("common.noImage")}</div>
                                            )}
                                        </div>
                                    </Link>

                                    {/* Content */}
                                    <div className={styles.content}>
                                        <Link to={`/listings/${listing.id}`} className={styles.titleLink}>
                                            <h3 className={styles.titleListing} title={listing.title}>{truncateText(listing.title)}</h3>
                                        </Link>
                                        <div className={styles.price}>{listing.price} €</div>
                                        <div className={styles.meta}>
                                            {listing.category?.name && (
                                                <span className={styles.tag}>{listing.category.name}</span>
                                            )}
                                            <span className={`${styles.statusBadge} ${styles[statusCfg.cls]}`}>
                                                {statusCfg?.key ? t(statusCfg.key) : ""}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Per-row actions */}
                                    <div className={styles.actions}>
                                        {listing.status === "active" && (
                                            <button className={styles.actionBtn} onClick={() => updateStatus([listing.id], "paused")}>{t("accountListings.pause")}</button>
                                        )}
                                        {listing.status === "paused" && (
                                            <button className={styles.actionBtn} onClick={() => updateStatus([listing.id], "active")}>{t("accountListings.resume")}</button>
                                        )}
                                        {listing.status !== "transaction_in_progress" && (
                                            <Link to={`/listings/${listing.id}/edit`} className={`${styles.actionBtn} ${styles.actionBtnPrimary}`}>{t("common.edit")}</Link>
                                        )}
                                        {listing.status !== "archived" ? (
                                            <button className={styles.actionBtn} onClick={() => updateStatus([listing.id], "archived")}>{t("accountListings.archive")}</button>
                                        ) : (
                                            <button className={styles.actionBtn} onClick={() => updateStatus([listing.id], "pend_app")}>{t("accountListings.unarchive")}</button>
                                        )}
                                        <button
                                            className={`${styles.actionBtn} ${styles.actionBtnDanger}`}
                                            onClick={() => window.confirm(t("accountListings.deleteOneConfirm")) && deleteListings([listing.id])}
                                        >
                                            {t("common.delete")}
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* ── Pagination ── */}
                    <div className={styles.pagination}>
                        <button
                            className={`${styles.pageBtn} ${!previous ? styles.pageBtnDisabled : ""}`}
                            disabled={!previous}
                            onClick={() => setPage((p) => p - 1)}
                        >
                            ← {t("common.previous")}
                        </button>
                        <span className={styles.pageInfo}>{t("common.pageOf", { page, total: totalPages })}</span>
                        <button
                            className={`${styles.pageBtn} ${!next ? styles.pageBtnDisabled : ""}`}
                            disabled={!next}
                            onClick={() => setPage((p) => p + 1)}
                        >
                            {t("common.next")} →
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}
